/**
 * createSessionLimitController — timer controller for the D4 Realtime voice
 * session caps: a hard wall-clock maximum duration and an idle disconnect, each
 * with a warning fired `warningLeadMs` beforehand.
 *
 * Pure and framework-free so the cap logic is unit-testable with fake timers in
 * isolation (the hook's WebRTC connect flow relies on real setTimeout(0)
 * macrotasks, which clash with fake timers). useRealtimeVoice owns one instance
 * and wires start()/registerActivity()/stop() to the connection lifecycle.
 *
 * The wall-clock is a MONOTONIC deadline the HOOK owns as an absolute timestamp
 * (sessionStartedAt): the hook passes the REMAINING budget to start() so an
 * automatic reconnect (401 auth-retry) resumes the same deadline instead of
 * getting a fresh full budget, while a user-initiated new session passes the
 * full duration. registerActivity resets ONLY the idle timers, never the
 * wall-clock.
 *
 * Callbacks:
 *   onWarn(reason)        — 'idle' | 'max_duration', fired warningLeadMs before a limit
 *   onClearIdleWarn()     — activity happened; the consumer may dismiss an idle warning
 *   onEnd(reason)         — 'idle' | 'max_duration', fired when a limit is reached
 */
import {
  MAX_SESSION_DURATION_MS,
  IDLE_DISCONNECT_MS,
  SESSION_LIMIT_WARNING_LEAD_MS,
} from './realtimeVoiceConstants';

/**
 * Remaining wall-clock budget for a session that began at `startedAtMs`, clamped
 * to [0, maxMs]. Used by the hook so a reconnect inherits the original deadline.
 */
export function computeRemainingMax(startedAtMs, nowMs, maxMs) {
  if (startedAtMs == null) return maxMs;
  return Math.max(0, Math.min(maxMs, maxMs - (nowMs - startedAtMs)));
}

export function createSessionLimitController({
  onWarn,
  onClearIdleWarn,
  onEnd,
  maxDurationMs = MAX_SESSION_DURATION_MS,
  idleMs = IDLE_DISCONNECT_MS,
  warningLeadMs = SESSION_LIMIT_WARNING_LEAD_MS,
} = {}) {
  let maxWarnTimer = null;
  let maxEndTimer = null;
  let idleWarnTimer = null;
  let idleEndTimer = null;
  let running = false;

  const clear = (t) => { if (t) clearTimeout(t); };

  function clearIdleTimers() {
    clear(idleWarnTimer);
    clear(idleEndTimer);
    idleWarnTimer = null;
    idleEndTimer = null;
  }

  function clearMaxTimers() {
    clear(maxWarnTimer);
    clear(maxEndTimer);
    maxWarnTimer = null;
    maxEndTimer = null;
  }

  // Reset the idle warn + end timers relative to "now". Called on start and on
  // every activity. Never touches the wall-clock timers.
  function armIdleTimers() {
    clearIdleTimers();
    if (idleMs > warningLeadMs) {
      idleWarnTimer = setTimeout(() => { if (onWarn) onWarn('idle'); }, idleMs - warningLeadMs);
    }
    idleEndTimer = setTimeout(() => { if (onEnd) onEnd('idle'); }, idleMs);
  }

  return {
    // remainingMaxMs = the wall-clock budget to arm for THIS (re)connection. The
    // hook passes the remaining time so a reconnect resumes the original deadline;
    // a fresh session passes the full maxDurationMs (the default).
    start(remainingMaxMs = maxDurationMs) {
      clearMaxTimers();
      running = true;
      const remaining = Math.max(0, remainingMaxMs);
      if (remaining > warningLeadMs) {
        maxWarnTimer = setTimeout(() => { if (onWarn) onWarn('max_duration'); }, remaining - warningLeadMs);
      } else if (remaining > 0 && onWarn) {
        // Already inside the warning-lead window (e.g. an auth-retry reconnect in the
        // final minute) — warn immediately so the heads-up is never skipped.
        onWarn('max_duration');
      }
      maxEndTimer = setTimeout(() => { if (onEnd) onEnd('max_duration'); }, remaining);
      armIdleTimers();
    },
    registerActivity() {
      if (!running) return;
      armIdleTimers();
      if (onClearIdleWarn) onClearIdleWarn();
    },
    stop() {
      running = false;
      clearMaxTimers();
      clearIdleTimers();
    },
  };
}
